import React, { useState, useEffect } from "react"
import DOMPurify from "dompurify"
import { isEmpty } from "lodash"
import PropTypes from "prop-types"
import { Icon } from "source/shared/components"
import {
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
  ComboboxPopover,
} from "@reach/combobox"
import { useDebounce } from "source/shared/hooks/useDebounce"
import { sessionApiClient } from "@planningcenter/cc-api-client"

const fieldErrorClass = (errors = {}) => {
  const hasErrors = !isEmpty(errors)

  return hasErrors ? "form-field--error" : ""
}

const ErrorMessages = ({ errors, className }) => {
  const hasErrors = errors && !isEmpty(errors)
  if (!hasErrors) return ""
  return Object.keys(errors)
    .map((key) => {
      const errorMessage = errors[key]

      if (!errorMessage && errorMessage !== 0) return null

      const errorType = `${key.charAt(0).toUpperCase()}${key.slice(1)}`.replace(
        "_",
        " ",
      )

      const displayMessage =
        typeof errorMessage === "object"
          ? Array.isArray(errorMessage)
            ? errorMessage.join(", ")
            : JSON.stringify(errorMessage)
          : String(errorMessage)

      return (
        <div className={`c-ruby ${className}`} key={key}>
          {errorType} {displayMessage}
        </div>
      )
    })
    .filter(Boolean)
}

const Field = ({
  id,
  hide,
  label,
  field_type: fieldType,
  required,
  children,
  errors,
  noLabel,
  help,
  fieldNote,
}) => {
  return hide ? null : (
    <div className="mt-3">
      {!noLabel && (
        <div className="d-f jc-sb">
          <label htmlFor={`${fieldType}_${id}`} className="f-1">
            {label}
            {required && <Required />}
          </label>
          {help && <div className="fs-4 c-tint2 f-r">{help}</div>}
        </div>
      )}
      {children}
      {fieldNote && <em className="fs-5 pt-4p">{fieldNote}</em>}
      <ErrorMessages errors={errors} className="mt-1" />
    </div>
  )
}

Field.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  hide: PropTypes.bool,
  label: PropTypes.node,
  field_type: PropTypes.string,
  required: PropTypes.bool,
  children: PropTypes.node,
  errors: PropTypes.object,
  noLabel: PropTypes.bool,
  help: PropTypes.node,
  fieldNote: PropTypes.node,
}

const Description = ({ description }) => {
  const withLineBreaks = DOMPurify.sanitize(description, {
    FORBID_TAGS: ["img", "figure", "figcaption"],
    FORBID_CONTENTS: ["figure", "figcaption"],
    ADD_ATTR: ["target"],
    FORCE_BODY: true,
  })

  return (
    description && (
      <p
        className="fs-4 c-tint2 mb-1 mt-4p lh-tight p-r b-8p"
        dangerouslySetInnerHTML={{
          __html: withLineBreaks.replace(/\n/g, "<br />"),
        }}
      ></p>
    )
  )
}

Description.propTypes = {
  description: PropTypes.string,
}

const Required = () => {
  return <span className="fs-3 c-ruby"> * </span>
}

const FieldGroup = ({ className = "", children }) => {
  return <div className={className}>{children}</div>
}

FieldGroup.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
}

const TextInput = ({ className = "", errors, id, placeholder, ...props }) => {
  return (
    <div
      className={`
        ${fieldErrorClass(errors)}
        ${className}
      `}
    >
      <input type="text" id={id} placeholder={placeholder} {...props} />
    </div>
  )
}

TextInput.propTypes = {
  className: PropTypes.string,
  errors: PropTypes.object,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
}

const TextArea = ({ errors, id, placeholder, ...props }) => {
  return (
    <div className={`${fieldErrorClass(errors)}`}>
      <textarea rows="5" id={id} placeholder={placeholder} {...props} />
    </div>
  )
}

TextArea.propTypes = {
  errors: PropTypes.object,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
}

const Checkbox = ({ errors, id, label, required, ...props }) => {
  const className = fieldErrorClass(errors)

  return (
    <div className={className}>
      <input type="checkbox" className="checkbox" id={id} {...props} />
      <label htmlFor={id} className="checkbox-label mb-4p mt-1">
        {label}
        {required && <Required />}
      </label>
    </div>
  )
}

Checkbox.propTypes = {
  errors: PropTypes.object,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.node,
  required: PropTypes.bool,
}
export default function CheckboxGroup({
  id,
  hasOtherOption,
  options,
  className,
  errors,
  onChange,
}) {
  const [selected, setSelected] = useState([])
  const [other, setOther] = useState("")
  const [showOther, setShowOther] = useState(false)

  useEffect(() => {
    if (showOther) {
      onChange({ selected, other })
    } else if (selected.length > 0) {
      onChange({ selected })
    } else {
      onChange({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, showOther, other])

  const handleCheckboxChange = (optionId, checked) => {
    if (checked) {
      setSelected((prev) => [...prev, optionId])
    } else {
      setSelected((prev) => prev.filter((id) => id !== optionId))
    }
  }

  const sortedOptions = [...options].sort(
    (a, b) => a.attributes.sequence - b.attributes.sequence,
  )

  return (
    <FieldGroup className={className}>
      {sortedOptions.map((option) => (
        <Checkbox
          key={option.id}
          id={`checkbox_${option.id}`}
          label={option.attributes.label}
          errors={errors}
          checked={selected.includes(option.id) ? "checked" : false}
          onChange={(e) => handleCheckboxChange(option.id, e.target.checked)}
        />
      ))}

      {hasOtherOption && (
        <Checkbox
          id={`other_${id}`}
          label="Other"
          errors={errors}
          checked={showOther}
          onChange={() => setShowOther((prev) => !prev)}
        />
      )}

      {showOther && (
        <div className="form-field__input--full">
          <TextInput
            placeholder="Other"
            onChange={(e) => setOther(e.target.value)}
          />
        </div>
      )}
    </FieldGroup>
  )
}

CheckboxGroup.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasOtherOption: PropTypes.bool,
  options: PropTypes.array.isRequired,
  className: PropTypes.string,
  errors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
}

const CheckboxProminent = ({ errors, id, label, required, ...props }) => {
  const className = `${fieldErrorClass(errors)}`
  return (
    <div className={className}>
      <input type="checkbox" className="checkbox" id={id} {...props} />
      <label htmlFor={id} className="checkbox-label c-tint0 fw-500 mb-4p mt-1">
        {label}
        {required && <Required />}
      </label>
    </div>
  )
}

CheckboxProminent.propTypes = {
  errors: PropTypes.object,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.node,
  required: PropTypes.bool,
}

const Select = ({
  id,
  children,
  className,
  defaultValue,
  errors,
  hasOtherOption,
  onChange,
  ...props
}) => {
  const [other, setOther] = useState("")
  const [selected, setSelected] = useState(defaultValue ? undefined : "")

  useEffect(() => {
    if (selected === "other") {
      onChange({ other })
    } else {
      onChange(selected)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [other, selected])

  return (
    <>
      <div className={`${className} ${fieldErrorClass(errors)} custom-select`}>
        <select
          className="select"
          id={id}
          onChange={(e) => setSelected(e.target.value)}
          {...(selected === undefined ? { defaultValue } : { value: selected })}
          {...props}
        >
          {!defaultValue && (
            <option value="" aria-label="No selection made" disabled>
              Select…
            </option>
          )}
          {children}
          {hasOtherOption && <option value="other">Other</option>}
        </select>
      </div>
      {selected === "other" && (
        <div className="mt-1">
          <TextInput
            placeholder="Other"
            value={other}
            onChange={(e) => setOther(e.target.value)}
          />
        </div>
      )}
    </>
  )
}

Select.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node,
  className: PropTypes.string,
  defaultValue: PropTypes.string,
  errors: PropTypes.object,
  hasOtherOption: PropTypes.bool,
  onChange: PropTypes.func,
}

const SectionHeader = ({ description, label }) => {
  const withLineBreaks = DOMPurify.sanitize(description, {
    FORBID_TAGS: ["img", "figure", "figcaption"],
    FORBID_CONTENTS: ["figure", "figcaption"],
    ADD_ATTR: ["target"],
    FORCE_BODY: true,
  })

  return (
    <div className="section-header pt-4 pb-1">
      <h2 className="h2">{label}</h2>
      {description && (
        <span
          className="fs-4 c-tint0"
          dangerouslySetInnerHTML={{
            __html: withLineBreaks.replace(/\n/g, "<br />"),
          }}
        ></span>
      )}
    </div>
  )
}

SectionHeader.propTypes = {
  description: PropTypes.string,
  label: PropTypes.node,
}

const FileInput = ({
  id,
  blockList,
  multiFileInput,
  onAddFile,
  onRemoveFile,
  onUploadErrors,
  maxTotalFileSize,
  totalFileSizeExceedsLimit,
}) => {
  const [filesSelected, setFilesSelected] = useState([])

  const fileInput = React.createRef()

  const isOversized = (file) => {
    return file.size > 1024 * 1024 * 10
  }

  const exceedsNumberLimit = (files) => {
    return [...filesSelected, ...files].length > 5
  }

  const isUnsupportedExtension = (file) => {
    const extension = file.name.split(".").pop().toLowerCase()
    return blockList.indexOf(extension) !== -1
  }

  const atFileCapacity = () => {
    return filesSelected.length === 5
  }

  const handleFileChange = ({ id }) => {
    const files = Object.values(fileInput.current.files).filter(
      (file) => !filesSelected.find((att) => att.name === file.name),
    )
    if (files.length === 0 || atFileCapacity()) {
      return
    }

    if (files.find((file) => isOversized(file))) {
      onUploadErrors({ attachments: "size cannot exceed 10 MB." })
      return
    }

    if (files.find((file) => isUnsupportedExtension(file))) {
      onUploadErrors({
        attachments: `type is not supported.`,
      })
      return
    }

    if (exceedsNumberLimit(files)) {
      onUploadErrors({ attachments: "can only include 5 attachments." })
      return
    }

    const newFilesSelected = multiFileInput
      ? [...filesSelected, ...files]
      : [files[0]]

    setFilesSelected(newFilesSelected)

    onAddFile({ form_field_id: id, files: newFilesSelected })
  }

  const handleFileRemove = ({ id, name }) => {
    fileInput.current.value = null
    const newFilesSelected = filesSelected.filter(
      (fileSelected) => fileSelected.name !== name,
    )
    setFilesSelected(newFilesSelected)
    onRemoveFile(id, name)
  }

  const formatFileSize = (bytes, precision = 0) => {
    if (bytes < 1048576) {
      return (bytes / 1024).toFixed(precision) + " KB"
    } else {
      return (bytes / 1048576).toFixed(precision) + " MB"
    }
  }

  const renderFileInput = ({ id }) => {
    const initialBtnMessage = `Choose file${multiFileInput ? "s" : ""}…`
    const updateBtnMessage = multiFileInput ? "Add files…" : initialBtnMessage
    const noSelectionMsg = `No file${
      multiFileInput ? "s" : ""
    } currently selected`
    const selectionMsg = `${filesSelected.length} file${
      filesSelected.length > 1 ? "s" : ""
    } currently selected`
    const isDisabled = atFileCapacity() || totalFileSizeExceedsLimit

    return (
      <div className="file-uploader">
        <div className="d-f fw-w">
          <div className="mr-2 mb-1">
            <button
              type="button"
              className={`btn minor-btn secondary-btn ${
                isDisabled ? "disabled-btn" : null
              }`}
              style={{ minWidth: "7rem" }}
              htmlFor={`field${id}`}
              onClick={() => fileInput.current?.click()}
              disabled={isDisabled}
            >
              {filesSelected.length > 0 ? updateBtnMessage : initialBtnMessage}
            </button>
            <input
              type="file"
              name={`person[data][${id}]`}
              ref={fileInput}
              onChange={() => handleFileChange({ id })}
              tabIndex={-1}
              className="screen-reader-text"
              id={`field${id}`}
              multiple={multiFileInput}
              disabled={isDisabled}
            />
          </div>
          <div className="fs-4 pt-2p c-tint2">
            <p className="mb-0">
              {filesSelected.length > 0 ? selectionMsg : noSelectionMsg}
            </p>
          </div>
        </div>
        <div className="fs-5 c-tint3 mb-1 lh-1.25">
          {totalFileSizeExceedsLimit ? (
            <div className="d-f">
              <span className="fs-6">
                <Icon
                  className="c-citrine mr-4p ml-4p mt-2p"
                  symbol="general#exclamation-triangle"
                />
              </span>
              <span>
                {`You have exceeded the maximum total file size for attachments on this
            form (${formatFileSize(maxTotalFileSize)}). To upload additional files, please remove some first.`}
              </span>
            </div>
          ) : (
            <>
              {multiFileInput ? (
                <span>Add up to 5 files.</span>
              ) : (
                <span>Choose 1 file.</span>
              )}{" "}
              <span>Maximum file size 10 MB.</span>
            </>
          )}
        </div>
        <ul className="unstyled mb-1">
          {filesSelected.map((fileSelected) => (
            <li className="d-f ai-c fs-4 pr-2 mb-1" key={fileSelected.name}>
              <div
                className="mr-1 lh-1 d-f ai-c jc-c c-tint4 o-h br-4p"
                style={{
                  width: 28,
                  height: 28,
                  flex: "0 0 28px",
                  border: "1px solid",
                }}
              >
                {["jpg", "jpeg", "gif", "png"].includes(
                  fileSelected.name.split(".").pop().toLowerCase(),
                ) ? (
                  <img
                    width={28}
                    height={28}
                    style={{ objectFit: "cover" }}
                    src={window.URL.createObjectURL(fileSelected)}
                    alt=""
                  />
                ) : (
                  <div className="fs-5 c-tint3 mt-2p">
                    <Icon symbol="general#outlined-generic-file" />
                  </div>
                )}
              </div>
              <div className="d-f fd-c overflow-text lh-1.25">
                <span className="overflow-text">{fileSelected.name}</span>
                <span className="fs-5 c-tint3">
                  {formatFileSize(fileSelected.size, 1)}
                </span>
              </div>
              <button
                type="button"
                className="btn minor-compact-btn destroy-btn ml-1 mb-2"
                style={{ marginBottom: 14 }}
                title={`Remove ${fileSelected.name}`}
                aria-label={`Remove ${fileSelected.name}`}
                onClick={() =>
                  handleFileRemove({ id, name: fileSelected.name })
                }
              >
                <Icon symbol="general#x" title="Remove" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  return <div>{renderFileInput({ id })}</div>
}

FileInput.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  blockList: PropTypes.array,
  multiFileInput: PropTypes.bool,
  onAddFile: PropTypes.func,
  onRemoveFile: PropTypes.func,
  onUploadErrors: PropTypes.func,
  maxTotalFileSize: PropTypes.number,
  totalFileSizeExceedsLimit: PropTypes.bool,
}

const AutocompleteAddressInput = ({
  address,
  updateAttribute,
  onAutocompleteSelect,
}) => {
  const [autocompletedAddresses, setAutocompletedAddresses] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  useEffect(() => {
    if (!debouncedSearchTerm) return

    sessionApiClient
      .post(`/people/v2/address_autocomplete`, {
        data: {
          attributes: {
            query: debouncedSearchTerm,
          },
        },
      })
      .then(({ data }) => {
        const autocompletedAddresses = data.map(
          ({ attributes: { text, place_identifier } }) => {
            return {
              name: text,
              place_identifier,
            }
          },
        )
        setAutocompletedAddresses(autocompletedAddresses)
      })
  }, [debouncedSearchTerm])

  function onAutocompletionSelect(place_identifier) {
    setSearchTerm("")
    setAutocompletedAddresses([])

    sessionApiClient
      .post(`/people/v2/autocomplete_detail`, {
        data: {
          attributes: {
            place_identifier,
          },
        },
      })
      .then(({ data }) => {
        onAutocompleteSelect(data.attributes)
      })
  }

  return (
    <div className="f-1">
      <label htmlFor="street_line_1" className="mt-1 mt-0@md pb-0">
        <span className="screen-reader-text">Street Address</span>
        <Combobox onSelect={onAutocompletionSelect}>
          <ComboboxInput
            style={{ borderLeft: "0px", borderRadius: "0px 4px 4px 0px" }}
            disabled={false}
            type="text"
            placeholder="Street Address"
            id="street_line_1"
            value={address}
            autocomplete={false}
            onChange={(e) => {
              const street = e.target.value
              setSearchTerm(street)

              updateAttribute(street)
            }}
          />
          <ComboboxPopover>
            <ComboboxList>
              {autocompletedAddresses.map(({ name, place_identifier }) => (
                <ComboboxOption
                  key={`address-${place_identifier}`}
                  value={place_identifier}
                >
                  {name}
                </ComboboxOption>
              ))}
            </ComboboxList>
          </ComboboxPopover>
        </Combobox>
      </label>
    </div>
  )
}

AutocompleteAddressInput.propTypes = {
  address: PropTypes.string,
  updateAttribute: PropTypes.func.isRequired,
  onAutocompleteSelect: PropTypes.func.isRequired,
}

export {
  Description,
  ErrorMessages,
  Field,
  FieldGroup,
  Required,
  TextInput,
  TextArea,
  CheckboxGroup,
  CheckboxProminent,
  Select,
  SectionHeader,
  FileInput,
  AutocompleteAddressInput,
}
